/* What step 1 says about the reader's own model-provider credential.
 *
 * Its own module so the step and its tests share one definition of the words rather than
 * each carrying a copy — the same reason src/auth/cliLoginCopy.tsx exists.
 *
 * Why this copy is on step 1 at all: auth through the gateway is two independent
 * credentials, the AI21 workspace key and the caller's own provider credential, and
 * readers were configuring the first *instead of* the second and getting 401 on their
 * first request. Step 1 is the only screen that precedes every connect path.
 */

import { type ReactNode } from "react";
import { type AgentKind } from "../../agents";

/* Carried alone until a tile is picked, so it has to work with no kind named. The picker
 * opens empty by design, and a reader who never picks one still sees this. */
export const PROVIDER_KEY_HEADLINE =
  "The Gateway doesn't replace your current AI subscription";

/* One line per kind, naming that kind's own credential — a reader who has to act on this
 * needs to know which account they're being told to keep, not that "a provider" exists.
 *
 * Exhaustive over AgentKind on purpose (the kinds.tsx convention): adding a kind should
 * fail the build here rather than silently ship a tile with nothing to say. `other` is
 * not a tile — it is reached by the link under the CTA — so it gets the generic line. */
export const PROVIDER_KEY_COPY: Record<AgentKind, ReactNode> = {
  claude_code: (
    <>
      You&apos;ll still need your Anthropic account. Make sure you&apos;re
      logged in before your first request so Claude Code can validate your
      session. AI21 never stores your credentials.
    </>
  ),
  codex: (
    <>
      You&apos;ll still need your ChatGPT or OpenAI account. Make sure
      you&apos;re logged in before your first request so Codex can validate your
      session. AI21 never stores your credentials.
    </>
  ),
  langchain: (
    <>
      You&apos;ll still need your own provider key. When you send your first
      request, your code will automatically pass it through. AI21 never stores
      this information.
    </>
  ),
  other: (
    <>
      You&apos;ll still need your own provider key. Your agent keeps sending it,
      the gateway passes it through untouched, and AI21 never stores it.
    </>
  ),
};
